import { Form, Formik, FormikHelpers } from 'formik'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useFileUploadUp } from 'shared/hooks/useFileUploadUp'
import { validateSchema } from '../model/validateSchema'
import { postAreas } from '../api/postAreas'
import { showNotification } from 'shared/redux/slice/notificationSlice'
import { AREASPROFILE } from 'shared/consts/routingConsts'
import { parseTimeRange } from 'features/Nomenclature/utils/parseTimeRange'
import { removeEmptyFields } from 'shared/utils/removeEmptyFields'
import { ToggleSwitch } from 'shared/components/ToggleSwitch'
import { initialValues } from '../model/initialValues'
import { TwoColumnLayout } from 'shared/components/TwoColumnLayout'
import { AreaNameSettings } from './AreaNameSettings'
import { StyledLine } from 'shared/styles/GlobalStyle'
import { AreasTariffSettings } from './AreasTariffSettings'
import { AreasGeneralSettings } from './AreasGeneralSettings'
import { AreasPhotos } from './AreasPhotos'

interface IPropsEmployeesCreate {
    setDisabled: (e: boolean) => void
    setLoading: (e: boolean) => void
    formikRef: React.Ref<FormikHelpers<any>>
}

interface IPropsFile {
    id: number
    name: string
    file?: File
}

export const AreasCreate = ({
    setDisabled,
    setLoading,
    formikRef,
}: IPropsEmployeesCreate) => {
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const [files, setFiles] = useState<IPropsFile[]>([])
    const [isBlocked, setIsBlocked] = useState(false)
    const [photo] = useState<File | null>(null)

    const { handleFileUpload } = useFileUploadUp()

    const onChangeToggleSwitch = () => {
        setIsBlocked(!isBlocked)
    }

    const handleFileChange = (newFiles: IPropsFile[]) => {
        setFiles(newFiles)
    }

    const handleSubmit = async (values: any, actions: any) => {
        setLoading(true)

        try {
            const { ad_price_info, ad_booking_info } = values
            const morningHours =
                ad_price_info && ad_price_info.morning_hours
                    ? parseTimeRange(ad_price_info.morning_hours)
                    : null
            const eveningHours =
                ad_price_info && ad_price_info.evening_hours
                    ? parseTimeRange(ad_price_info.evening_hours)
                    : null
            const available_booking_time =
                ad_booking_info && ad_booking_info.available_booking_time
                    ? parseTimeRange(ad_booking_info.available_booking_time)
                    : null
            const available_online_booking_time =
                ad_booking_info && ad_booking_info.available_online_booking_time
                    ? parseTimeRange(
                        ad_booking_info.available_online_booking_time,
                    )
                    : null
            const { uploadedFilesInfo } = await handleFileUpload(
                photo,
                files?.map(file => file.file!) || [],
            )

            const filteredValues = removeEmptyFields({
                ...values,
                equipment: values.equipment || [],
                ad_booking_info: ad_booking_info
                    ? {
                        ...ad_booking_info,
                        available_booking_time: available_booking_time
                            ? available_booking_time
                            : null,
                        available_online_booking_time:
                            available_online_booking_time
                                ? available_online_booking_time
                                : null,
                        minimum_booking_time_minutes:
                            ad_booking_info.minimum_booking_time_minutes ||
                            null,
                    }
                    : null,
                ad_price_info: ad_price_info
                    ? {
                        ...ad_price_info,
                        morning_hours: morningHours ? morningHours[0] : null,
                        evening_hours: eveningHours ? eveningHours[0] : null,
                    }
                    : null,
                photos_hash_names:
                    uploadedFilesInfo && files
                        ? uploadedFilesInfo.map(info => info.hash_name)
                        : [],
                is_locked: isBlocked,
            })

            const profileResponse = await postAreas({
                ...filteredValues,
            })

            if (profileResponse.status !== 201) {
                dispatch(
                    showNotification({
                        message: 'Ошибка',
                        type: 'error',
                    }),
                )
                return
            }

            dispatch(
                showNotification({
                    message: 'Успешно',
                    type: 'success',
                }),
            )

            navigate(`${AREASPROFILE}/${profileResponse.data.id}`)
            actions.setSubmitting(false)
        } catch (err) {
            console.error(err)
            actions.setSubmitting(false)
        } finally {
            setLoading(false)
        }
    }

    return (
        <Formik
            innerRef={formikRef as any}
            key='personalForm'
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validateSchema}
        >
            {({ isValid }) => {
                setDisabled(!isValid)
                return (
                    <Form>
                        <div className="title">Помещение</div>
                        <AreaNameSettings isEditable={true} />
                        <StyledLine margin='20px 0' />
                        <TwoColumnLayout>
                            <AreasTariffSettings
                                isEditable={true}
                                AreaPhotos={
                                    <AreasPhotos
                                        isEditable={true}
                                        cachedPhotos={[]}
                                        onChangeFile={handleFileChange}
                                        actions={[
                                            <ToggleSwitch
                                                key='toggle-switch'
                                                active={isBlocked}
                                                title='Функция блокирования'
                                                onChange={onChangeToggleSwitch}
                                            />,
                                        ]}
                                    />
                                }
                            />
                            <AreasGeneralSettings isEditable={true} />
                        </TwoColumnLayout>
                    </Form>
                )
            }}
        </Formik>
    )
}
